"use client";

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/auth-context";
import { api } from "@/lib/api";

const RegionContext = createContext();

/**
 * Normaliseert een omgeving (PurchasingOrganization) naar de shape die de
 * selector en alle tenant-filters verwachten.
 */
function toEnvironment(org) {
  const displayName = org.displayName ?? null;
  return {
    uuid: org.uuid,
    // Officiële naam (ongewijzigd, blijft beschikbaar voor formele weergave).
    name: org.name,
    // Werknaam/roepnaam waaronder aanbieders de organisatie kennen (optioneel).
    displayName,
    // Afgeleid label voor weergave: werknaam indien aanwezig, anders de
    // officiële naam. Gebruik dit in selectors/headers i.p.v. name.
    label: displayName || org.name,
    isDemo: org.isDemo ?? false,
    imageUrl: org.imageUrl ?? null,
  };
}

export function RegionProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [environments, setEnvironments] = useState([]);

  // Haal de beschikbare omgevingen op via de tenant-gefilterde endpoint.
  // Super admins krijgen alle actieve omgevingen; andere gebruikers alleen
  // hun eigen (backend: PurchasingOrganizationCollectionProvider).
  useEffect(() => {
    if (!isAuthenticated) {
      setEnvironments([]);
      return;
    }

    let cancelled = false;

    const fetchEnvironments = async () => {
      try {
        const { data } = await api.get("/api/purchasing-organizations");
        const members = data?.["hydra:member"] ?? data?.member ?? (Array.isArray(data) ? data : []);
        const mapped = members
          .filter((org) => org?.uuid && org.isActive !== false)
          .map(toEnvironment);

        if (!cancelled) {
          setEnvironments(mapped);
        }
      } catch {
        // Endpoint niet beschikbaar: geen omgevingen (fail-safe).
        if (!cancelled) {
          setEnvironments([]);
        }
      }
    };

    fetchEnvironments();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Aanbieders (geen beheerder/accountant/superadmin) werken vanuit één
  // totaalomgeving over al hun inkooporganisaties. Zij krijgen GEEN actieve
  // tenant toegewezen: er is geen omgeving-selector en al hun pagina's zijn
  // tenant-overstijgend (overzicht, indieningen, opgeslagen, bestanden, tickets,
  // wizard, mijn organisatie). Zo raken ze nooit stil "vastgepind" op één tenant.
  // POSITIEF bepaald op de provider-rollen (niet via negatie), zodat gemeente-/
  // organisatie-rollen niet onbedoeld als aanbieder worden behandeld.
  const isProvider =
    user?.roles?.includes("ROLE_PROVIDER_ADMIN") ||
    user?.roles?.includes("ROLE_PROVIDER_USER") ||
    user?.roles?.includes("ROLE_HEALTHCARE") ||
    false;

  // Bepaal de actieve omgeving zodra de lijst geladen is.
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Voor aanbieders: nooit automatisch een tenant kiezen. Ruim een eventueel
    // eerder opgeslagen keuze op zodat er geen X-Tenant-Context wordt meegestuurd.
    if (isProvider) {
      setSelectedRegion(null);
      localStorage.removeItem("selectedRegion");
      return;
    }

    if (environments.length === 0) return;

    const storedRegion = localStorage.getItem("selectedRegion");
    let validRegion = null;

    if (storedRegion) {
      try {
        const parsed = JSON.parse(storedRegion);
        const match = environments.find((env) => env.uuid === parsed.uuid);
        if (match) {
          validRegion = match;
        } else {
          localStorage.removeItem("selectedRegion");
        }
      } catch {
        localStorage.removeItem("selectedRegion");
      }
    }

    const next = validRegion ?? environments[0];
    setSelectedRegion(next);
    localStorage.setItem("selectedRegion", JSON.stringify(next));
  }, [environments, isProvider]);

  useEffect(() => {
    if (selectedRegion) {
      localStorage.setItem("selectedRegion", JSON.stringify(selectedRegion));
    }
  }, [selectedRegion]);

  // Memoïseer de context-value zodat useRegion()-consumers (o.a. de zware
  // lijst-views en hun columns-memo's) niet her-renderen bij elke provider-render.
  const value = useMemo(
    () => ({ selectedRegion, setSelectedRegion, environments }),
    [selectedRegion, environments]
  );

  return (
      <RegionContext.Provider value={value}>
        {children}
      </RegionContext.Provider>
  );
}

export function useRegion() {
  return useContext(RegionContext);
}
